/**
 * GPT Image 2 / 2.5 custom sizes. The provider accepts any WIDTHxHEIGHT inside
 * `size_rules` (multiples of 16, edges <= 3840, 655,360..8,294,400 pixels,
 * ratio within 1:3..3:1); Studio composes one from an aspect ratio and a
 * 1K/2K/4K tier so the customer never types pixel values.
 */
export interface ImageSizeRules {
  multiple: number;
  max_edge: number;
  min_pixels: number;
  max_pixels: number;
  max_ratio: number;
  experimental_above?: string;
}

export const DEFAULT_IMAGE_SIZE_RULES: ImageSizeRules = {
  multiple: 16,
  max_edge: 3840,
  min_pixels: 655_360,
  max_pixels: 8_294_400,
  max_ratio: 3,
  experimental_above: "2560x1440",
};

/**
 * Target output area per tier: 1K = 1024², 4K = UHD (the provider maximum).
 * 2K is the largest non-experimental area (2560×1440 = 3,686,400 px): above it
 * GPT Image 2.5 at "high" measurably painted fine texture (2026-10-08), so 2K
 * never crosses the experimental line and 9:16 composes 1440×2560.
 */
const RESOLUTION_AREAS: Record<string, number> = {
  "1K": 1024 * 1024,
  "2K": 2560 * 1440,
  "4K": 3840 * 2160,
};

function experimentalPixels(rules: ImageSizeRules): number {
  const size = parseImageSize(rules.experimental_above);
  return size ? size.width * size.height : Number.POSITIVE_INFINITY;
}

/** True for a WIDTHxHEIGHT above the provider's experimental line. */
export function isExperimentalSize(value: unknown, rules: ImageSizeRules = DEFAULT_IMAGE_SIZE_RULES): boolean {
  const size = parseImageSize(value);
  return size !== null && size.width * size.height > experimentalPixels(rules);
}

export const AUTO_SIZE = "auto";

export function parseRatio(ratio: string): number | null {
  const match = /^(\d+):(\d+)$/.exec(ratio.trim());
  if (!match) return null;
  const width = Number(match[1]);
  const height = Number(match[2]);
  return width > 0 && height > 0 ? width / height : null;
}

export function parseImageSize(value: unknown): { width: number; height: number } | null {
  if (typeof value !== "string") return null;
  const match = /^(\d+)x(\d+)$/i.exec(value.trim());
  if (!match) return null;
  const width = Number(match[1]);
  const height = Number(match[2]);
  return width > 0 && height > 0 ? { width, height } : null;
}

export function isValidImageSize(value: unknown, rules: ImageSizeRules = DEFAULT_IMAGE_SIZE_RULES): boolean {
  if (value === AUTO_SIZE) return true;
  const size = parseImageSize(value);
  if (!size) return false;
  const { width, height } = size;
  if (width % rules.multiple || height % rules.multiple) return false;
  if (Math.max(width, height) > rules.max_edge) return false;
  const pixels = width * height;
  if (pixels < rules.min_pixels || pixels > rules.max_pixels) return false;
  return Math.max(width, height) <= rules.max_ratio * Math.min(width, height);
}

/** Compose a contract-valid WIDTHxHEIGHT; "auto" ratio defers to the provider. */
export function composeImageSize(ratio: string, resolution: string, rules: ImageSizeRules = DEFAULT_IMAGE_SIZE_RULES): string {
  const aspect = parseRatio(ratio);
  if (ratio === AUTO_SIZE || aspect === null) return AUTO_SIZE;
  const area = Math.min(RESOLUTION_AREAS[resolution] ?? RESOLUTION_AREAS["1K"], rules.max_pixels);
  let width = Math.sqrt(area * aspect);
  let height = width / aspect;
  const scale = Math.min(1, rules.max_edge / Math.max(width, height));
  width *= scale;
  height *= scale;
  const step = rules.multiple;
  let roundedWidth = Math.max(step, Math.round(width / step) * step);
  let roundedHeight = Math.max(step, Math.round(height / step) * step);
  // Rounding can nudge the area past the provider ceiling (or, for a tier that
  // sits at or below the experimental line, past that line); shave the long edge.
  const limit = experimentalPixels(rules);
  const ceiling = area <= limit ? Math.min(rules.max_pixels, limit) : rules.max_pixels;
  for (let guard = 0; roundedWidth * roundedHeight > ceiling && guard < 64; guard += 1) {
    if (roundedWidth >= roundedHeight) roundedWidth -= step;
    else roundedHeight -= step;
  }
  for (let guard = 0; roundedWidth * roundedHeight < rules.min_pixels && guard < 64; guard += 1) {
    if (roundedWidth <= roundedHeight) roundedWidth += step;
    else roundedHeight += step;
  }
  return `${roundedWidth}x${roundedHeight}`;
}

/** Nearest ratio and tier for an existing size, so restored drafts and presets read naturally. */
export function describeImageSize(
  value: unknown,
  ratios: string[],
  resolutions: string[]
): { ratio: string; resolution: string | null } {
  const size = parseImageSize(value);
  if (!size) return { ratio: AUTO_SIZE, resolution: null };
  const aspect = size.width / size.height;
  const candidates = ratios.filter((ratio) => parseRatio(ratio) !== null);
  const ratio = candidates.reduce((best, candidate) => {
    const distance = Math.abs(Math.log(parseRatio(candidate)!) - Math.log(aspect));
    return distance < best.distance ? { ratio: candidate, distance } : best;
  }, { ratio: candidates[0] ?? AUTO_SIZE, distance: Number.POSITIVE_INFINITY }).ratio;
  const area = size.width * size.height;
  const resolution = resolutions.reduce((best, candidate) => {
    const target = RESOLUTION_AREAS[candidate];
    if (!target) return best;
    const distance = Math.abs(Math.log(target) - Math.log(area));
    return distance < best.distance ? { resolution: candidate, distance } : best;
  }, { resolution: resolutions[0] ?? null, distance: Number.POSITIVE_INFINITY }).resolution;
  return { ratio, resolution };
}

export function imageSizeLabel(value: unknown): string {
  const size = parseImageSize(value);
  return size ? `${size.width}×${size.height}` : "Auto";
}

/**
 * Seedream sizes: the provider documents one exact WIDTHxHEIGHT per level and
 * aspect ratio (`size_table`), so Studio sends those values verbatim. "auto"
 * sends the level itself ("2K") and lets the prompt describe the ratio.
 */
export type ImageSizeTable = Record<string, Record<string, string>>;

export function composeFromTable(table: ImageSizeTable, ratio: string, resolution: string): string {
  if (ratio === AUTO_SIZE) return resolution;
  return table[resolution]?.[ratio] ?? resolution;
}

export function describeFromTable(
  table: ImageSizeTable,
  value: unknown,
  ratios: string[],
  resolutions: string[]
): { ratio: string; resolution: string | null } {
  if (typeof value === "string" && resolutions.includes(value)) return { ratio: AUTO_SIZE, resolution: value };
  for (const resolution of resolutions) {
    for (const [ratio, size] of Object.entries(table[resolution] ?? {})) {
      if (size === value) return { ratio, resolution };
    }
  }
  // A typed exact size: nearest documented ratio, and the level whose 1:1
  // area is closest to it.
  const size = parseImageSize(value);
  if (!size) return { ratio: AUTO_SIZE, resolution: resolutions[0] ?? null };
  const nearest = describeImageSize(value, ratios, []);
  const area = size.width * size.height;
  const resolution = resolutions.reduce((best, candidate) => {
    const square = parseImageSize(table[candidate]?.["1:1"]);
    if (!square) return best;
    const distance = Math.abs(Math.log(square.width * square.height) - Math.log(area));
    return distance < best.distance ? { resolution: candidate, distance } : best;
  }, { resolution: resolutions[0] ?? null, distance: Number.POSITIVE_INFINITY }).resolution;
  return { ratio: nearest.ratio, resolution };
}
